// MeasurementsMap.js - Fixed to handle null geofence polygons
'use client';

import React, { useEffect, useRef, useCallback, useState } from 'react';
import { GoogleMap, useLoadScript, PolygonF } from '@react-google-maps/api';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { BoltIcon, LightBulbIcon, MoonIcon, SunIcon } from '@heroicons/react/24/outline';

const MAX_MAP_MARKERS = 10000;

// Default quality thresholds
const DEFAULT_THRESHOLDS = {
  compaction: { pass: 92.0, warning: 88.0, fail: 0.0 },
  density: { pass: 145.0, warning: 140.0, fail: 0.0 }
};

// Marker icons
const createMarkerIcon = (color) => ({
  path: 'M10 20c-5.523 0-10-4.477-10-10S4.477 0 10 0s10 4.477 10 10-4.477 10-10 10z',
  fillColor: color,
  fillOpacity: 1,
  strokeWeight: 2,
  strokeColor: '#FFFFFF',
  scale: 0.6,
});

const qualityMarkerIcons = {
  pass: createMarkerIcon('#10B981'),    // Green
  warning: createMarkerIcon('#F59E0B'), // Orange
  fail: createMarkerIcon('#EF4444'),    // Red
  unknown: createMarkerIcon('#6B7280'), // Gray
};

const activeMarkerIcon = createMarkerIcon('#00FF00'); // Bright green

// Cluster renderer
const createClusterRenderer = () => {
  if (!window.google || !window.google.maps) return null;

  return {
    render: ({ count, position }) => {
      const color = count > 500 ? "#dc2626" : count > 100 ? "#ea580c" : count > 25 ? "#d97706" : "#059669";
      const size = count > 500 ? 70 : count > 100 ? 60 : count > 25 ? 50 : 40;
      
      return new window.google.maps.Marker({
        position,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          fillColor: color,
          fillOpacity: 0.8,
          strokeWeight: 2,
          strokeColor: "#ffffff",
          scale: size / 2,
        },
        label: {
          text: String(count),
          color: "white",
          fontSize: count > 100 ? "14px" : "12px",
          fontWeight: "bold",
        },
        title: `${count} measurements`,
        zIndex: 1000,
      });
    },
  };
};

const MeasurementsMap = ({
  measurements = [],
  selectedMeasurement = null,
  geofencePolygon = null,
  mapCenter = { lat: 42.73116, lng: -73.68657 },
  mapZoom = 13,
  onMarkerClick = () => {},
  onOpenProject = () => {},
  
  // Quality control props
  qualityAttribute = 'compaction',
  qualityThresholds = DEFAULT_THRESHOLDS,
  showQualityColors = true,
  
  // Map control props
  showStyleControls = true,
  showMeasurementCount = true,
  
  // Height and styling
  height = '60vh',
  className = '',
}) => {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: 'AIzaSyBBfpoVAWYZaujXG5t93mFIJKqk3cwHynw',
  });

  const mapRef = useRef(null);
  const markerClustererRef = useRef(null);
  const markersRef = useRef([]);
  const infoWindowRef = useRef(null);
  const [activeMarkerId, setActiveMarkerId] = useState(null);
  
  // Internal map style state
  const [currentMapStyle, setCurrentMapStyle] = useState([]);

  // Quality status determination
  const getQualityStatus = useCallback((measurement, attribute, thresholds) => {
    const value = measurement[attribute === 'compaction' ? 'Compaction' : 'Density'];
    
    if (value === null || value === undefined || isNaN(value)) {
      return 'unknown';
    }

    const limits = thresholds[attribute];
    
    if (value >= limits.pass) return 'pass';
    if (value >= limits.warning) return 'warning';
    return 'fail';
  }, []);

  // Cleanup markers
  const cleanupMarkers = useCallback(() => {
    if (markerClustererRef.current) {
      markerClustererRef.current.clearMarkers();
      markerClustererRef.current = null;
    }
    
    markersRef.current.forEach(marker => {
      if (marker.clickListener) {
        window.google?.maps?.event?.removeListener(marker.clickListener);
      }
      marker.setMap(null);
    });
    markersRef.current = [];
    
    if (infoWindowRef.current) {
      infoWindowRef.current.close();
    }
  }, []);

  // Create markers with clustering
  const createMarkersWithClustering = useCallback((measurementsData) => {
    if (!mapRef.current || !isLoaded || !measurementsData.length) return;
    
    if (!window.google || !window.google.maps) {
      console.warn('Google Maps API not loaded yet');
      return;
    }

    // Wait for map to be fully ready
    if (!mapRef.current.getProjection()) {
      setTimeout(() => createMarkersWithClustering(measurementsData), 200);
      return;
    }

    cleanupMarkers();

    if (!infoWindowRef.current) {
      infoWindowRef.current = new window.google.maps.InfoWindow();
    }

    const markers = [];
    const validMeasurements = measurementsData
      .filter(m => m.latitude && m.longitude)
      .slice(0, MAX_MAP_MARKERS);

    validMeasurements.forEach((measurement) => {
      try {
        // Determine marker icon
        let markerIcon;
        if (showQualityColors) {
          const qualityStatus = getQualityStatus(measurement, qualityAttribute, qualityThresholds);
          markerIcon = qualityMarkerIcons[qualityStatus];
        } else {
          markerIcon = qualityMarkerIcons.unknown;
        }

        // Override with active marker if selected
        if (measurement.id === activeMarkerId || measurement.id === selectedMeasurement) {
          markerIcon = activeMarkerIcon;
        }
        
        const marker = new window.google.maps.Marker({
          position: { 
            lat: Number(measurement.latitude), 
            lng: Number(measurement.longitude) 
          },
          icon: markerIcon,
          title: measurement.Description1 || 'Measurement',
          map: null,
          optimized: true,
          clickable: true
        });

        if (!marker) return;

        marker.measurement = measurement;

        // Add click listener
        try {
          const clickListener = marker.addListener('click', function() {
            try {
              setActiveMarkerId(measurement.id);
              
              // Update all marker icons
              markersRef.current.forEach(m => {
                if (m && m.measurement && m.setIcon) {
                  try {
                    const isActive = m.measurement.id === measurement.id || m.measurement.id === selectedMeasurement;
                    if (isActive) {
                      m.setIcon(activeMarkerIcon);
                    } else if (showQualityColors) {
                      const status = getQualityStatus(m.measurement, qualityAttribute, qualityThresholds);
                      m.setIcon(qualityMarkerIcons[status]);
                    } else {
                      m.setIcon(qualityMarkerIcons.unknown);
                    }
                  } catch (iconError) {
                    console.warn('Error updating marker icon:', iconError);
                  }
                }
              });

              // Create info window content
              const qualityStatus = showQualityColors 
                ? getQualityStatus(measurement, qualityAttribute, qualityThresholds)
                : 'unknown';
              const attributeValue = measurement[qualityAttribute === 'compaction' ? 'Compaction' : 'Density'];
              const attributeLabel = qualityAttribute === 'compaction' ? 'Compaction' : 'Density';
              const statusColor = qualityStatus === 'pass' ? '#10B981' : 
                                 qualityStatus === 'warning' ? '#F59E0B' : '#EF4444';

              const contentString = `
                <div class="p-3 bg-white rounded-lg shadow-md" style="min-width: 250px;">
                  <div class="flex items-center justify-start mb-3">
                    <h3 class="text-base font-bold text-gray-800">Measurement Details</h3>
                  </div>
                  <div class="space-y-2">
                    <p class="text-sm">
                      <span class="font-medium text-gray-700">Density:</span>
                      <span class="ml-1 text-gray-900">${measurement.Density?.toFixed(1) || 'N/A'}</span>
                    </p>
                    <p class="text-sm">
                      <span class="font-medium text-gray-700">Compaction:</span>
                      <span class="ml-1 text-gray-900">${measurement.Compaction?.toFixed(1) || 'N/A'}%</span>
                    </p>
                    ${showQualityColors ? `
                    <p class="text-sm">
                      <span class="font-medium text-gray-700">Quality Status:</span>
                      <span class="ml-1 font-medium" style="color: ${statusColor}">
                        ${qualityStatus.charAt(0).toUpperCase() + qualityStatus.slice(1)} 
                        (${attributeLabel}: ${attributeValue?.toFixed(1) || 'N/A'})
                      </span>
                    </p>
                    ` : ''}
                    <p class="text-sm">
                      <span class="font-medium text-gray-700">Surface Temp:</span>
                      <span class="ml-1 text-gray-900">${measurement.SurfaceTemp?.toFixed(1) || 'N/A'}</span>
                    </p>
                    <p class="text-sm">
                      <span class="font-medium text-gray-700">Operator:</span>
                      <span class="ml-1 text-gray-900">${measurement.operator_name || 'N/A'}</span>
                    </p>
                    <p class="text-sm">
                      <span class="font-medium text-gray-700">Time:</span>
                      <span class="ml-1 text-gray-900">${new Date(measurement.LocalTimeOfMeasurement).toLocaleString()}</span>
                    </p>
                  </div>
                  ${onOpenProject ? `
                  <div class="mt-3 pt-3 border-t border-gray-200">
                    <button onclick="window.openMeasurementProject('${measurement.id}')" 
                            class="w-full px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors">
                      View Project
                    </button>
                  </div>
                  ` : ''}
                </div>
              `;

              if (infoWindowRef.current?.setContent) {
                infoWindowRef.current.setContent(contentString);
                infoWindowRef.current.open(mapRef.current, marker);
              }

              // Call external click handler
              onMarkerClick(measurement);
              
            } catch (clickError) {
              console.error('Error in marker click handler:', clickError);
            }
          });

          marker.clickListener = clickListener;
        } catch (listenerError) {
          console.error('Error adding click listener:', listenerError);
        }

        markers.push(marker);
        
      } catch (markerError) {
        console.error('Error creating marker:', markerError);
      }
    });

    markersRef.current = markers;

    // Create clusterer if we have markers
    if (markers.length > 0 && mapRef.current.getProjection()) {
      try {
        const clusterRenderer = createClusterRenderer();
        if (clusterRenderer) {
          markerClustererRef.current = new MarkerClusterer({
            map: mapRef.current,
            markers: markers,
            renderer: clusterRenderer,
            gridSize: 80,
            maxZoom: 14,
            minimumClusterSize: 3,
            averageCenter: true,
            zoomOnClick: true,
          });
        }
      } catch (clusterError) {
        console.error('Error creating marker clusterer:', clusterError);
        // Fallback to individual markers
        markers.forEach(marker => marker.setMap(mapRef.current));
      }
    }
  }, [isLoaded, qualityAttribute, qualityThresholds, showQualityColors, activeMarkerId, selectedMeasurement, getQualityStatus, onMarkerClick]);

  // Global open project function
  useEffect(() => {
    if (onOpenProject) {
      window.openMeasurementProject = onOpenProject;
      return () => { delete window.openMeasurementProject; };
    }
  }, [onOpenProject]);

  // Recreate markers when measurements or quality settings change
  useEffect(() => {
    createMarkersWithClustering(measurements);
  }, [measurements, createMarkersWithClustering]);

  // Handle external measurement selection
  useEffect(() => {
    if (selectedMeasurement && measurements.length > 0) {
      const measurement = measurements.find(m => m.id === selectedMeasurement);
      if (measurement?.latitude && measurement?.longitude && mapRef.current) {
        mapRef.current.setCenter({ 
          lat: measurement.latitude, 
          lng: measurement.longitude 
        });
        mapRef.current.setZoom(18);
        
        setActiveMarkerId(selectedMeasurement);
        
        // Update marker icons and open info window
        setTimeout(() => {
          markersRef.current.forEach(marker => {
            if (marker.measurement) {
              const isActive = marker.measurement.id === selectedMeasurement;
              marker.setIcon(isActive ? activeMarkerIcon : 
                showQualityColors ? 
                  qualityMarkerIcons[getQualityStatus(marker.measurement, qualityAttribute, qualityThresholds)] :
                  qualityMarkerIcons.unknown
              );
            }
          });

          // Open info window for selected measurement
          const selectedMarker = markersRef.current.find(m => m.measurement?.id === selectedMeasurement);
          if (selectedMarker && infoWindowRef.current) {
            // Trigger click to open info window
            window.google.maps.event.trigger(selectedMarker, 'click');
          }
        }, 500);
      }
    }
  }, [selectedMeasurement, measurements, qualityAttribute, qualityThresholds, showQualityColors, getQualityStatus]);

  // Cleanup on unmount
  useEffect(() => {
    return () => cleanupMarkers();
  }, [cleanupMarkers]);

  // Map style definitions
  const mapStyles = {
    default: [],
    night: [
      { elementType: "geometry", stylers: [{ color: "#242f3e" }] },
      { elementType: "labels.text.stroke", stylers: [{ color: "#242f3e" }] },
      { elementType: "labels.text.fill", stylers: [{ color: "#746855" }] },
      {
        featureType: "administrative.locality",
        elementType: "labels.text.fill",
        stylers: [{ color: "#d59563" }],
      },
      {
        featureType: "poi",
        elementType: "labels.text.fill",
        stylers: [{ color: "#d59563" }],
      },
      {
        featureType: "poi.park",
        elementType: "geometry",
        stylers: [{ color: "#263c3f" }],
      },
      {
        featureType: "poi.park",
        elementType: "labels.text.fill",
        stylers: [{ color: "#6b9a76" }],
      },
      {
        featureType: "road",
        elementType: "geometry",
        stylers: [{ color: "#38414e" }],
      },
      {
        featureType: "road",
        elementType: "geometry.stroke",
        stylers: [{ color: "#212a37" }],
      },
      {
        featureType: "road",
        elementType: "labels.text.fill",
        stylers: [{ color: "#9ca5b3" }],
      },
      {
        featureType: "road.highway",
        elementType: "geometry",
        stylers: [{ color: "#746855" }],
      },
      {
        featureType: "road.highway",
        elementType: "geometry.stroke",
        stylers: [{ color: "#1f2835" }],
      },
      {
        featureType: "road.highway",
        elementType: "labels.text.fill",
        stylers: [{ color: "#f3d19c" }],
      },
      {
        featureType: "transit",
        elementType: "geometry",
        stylers: [{ color: "#2f3948" }],
      },
      {
        featureType: "transit.station",
        elementType: "labels.text.fill",
        stylers: [{ color: "#d59563" }],
      },
      {
        featureType: "water",
        elementType: "geometry",
        stylers: [{ color: "#17263c" }],
      },
      {
        featureType: "water",
        elementType: "labels.text.fill",
        stylers: [{ color: "#515c6d" }],
      },
      {
        featureType: "water",
        elementType: "labels.text.stroke",
        stylers: [{ color: "#17263c" }],
      },
    ],
    aubergine: [
      { elementType: "geometry", stylers: [{ color: "#1d2c4d" }] },
      { elementType: "labels.text.fill", stylers: [{ color: "#8ec3b9" }] },
      { elementType: "labels.text.stroke", stylers: [{ color: "#1a3646" }] },
      {
        featureType: "administrative.country",
        elementType: "geometry.stroke",
        stylers: [{ color: "#4b6878" }],
      },
      {
        featureType: "administrative.land_parcel",
        elementType: "labels.text.fill",
        stylers: [{ color: "#64779e" }],
      },
      {
        featureType: "administrative.province",
        elementType: "geometry.stroke",
        stylers: [{ color: "#4b6878" }],
      },
      {
        featureType: "landscape.man_made",
        elementType: "geometry.stroke",
        stylers: [{ color: "#334e87" }],
      },
      {
        featureType: "landscape.natural",
        elementType: "geometry",
        stylers: [{ color: "#023e58" }],
      },
      {
        featureType: "poi",
        elementType: "geometry",
        stylers: [{ color: "#283d6a" }],
      },
      {
        featureType: "poi",
        elementType: "labels.text.fill",
        stylers: [{ color: "#6f9ba5" }],
      },
      {
        featureType: "poi",
        elementType: "labels.text.stroke",
        stylers: [{ color: "#1d2c4d" }],
      },
      {
        featureType: "poi.park",
        elementType: "geometry.fill",
        stylers: [{ color: "#023e58" }],
      },
      {
        featureType: "poi.park",
        elementType: "labels.text.fill",
        stylers: [{ color: "#3C7680" }],
      },
      {
        featureType: "road",
        elementType: "geometry",
        stylers: [{ color: "#304a7d" }],
      },
      {
        featureType: "road",
        elementType: "labels.text.fill",
        stylers: [{ color: "#98a5be" }],
      },
      {
        featureType: "road",
        elementType: "labels.text.stroke",
        stylers: [{ color: "#1d2c4d" }],
      },
      {
        featureType: "road.highway",
        elementType: "geometry",
        stylers: [{ color: "#2c6675" }],
      },
      {
        featureType: "road.highway",
        elementType: "geometry.stroke",
        stylers: [{ color: "#255763" }],
      },
      {
        featureType: "road.highway",
        elementType: "labels.text.fill",
        stylers: [{ color: "#b0d5ce" }],
      },
      {
        featureType: "road.highway",
        elementType: "labels.text.stroke",
        stylers: [{ color: "#023e58" }],
      },
      {
        featureType: "transit",
        elementType: "labels.text.fill",
        stylers: [{ color: "#98a5be" }],
      },
      {
        featureType: "transit",
        elementType: "labels.text.stroke",
        stylers: [{ color: "#1d2c4d" }],
      },
      {
        featureType: "transit.line",
        elementType: "geometry.fill",
        stylers: [{ color: "#283d6a" }],
      },
      {
        featureType: "transit.station",
        elementType: "geometry",
        stylers: [{ color: "#3a4762" }],
      },
      {
        featureType: "water",
        elementType: "geometry",
        stylers: [{ color: "#0e1626" }],
      },
      {
        featureType: "water",
        elementType: "labels.text.fill",
        stylers: [{ color: "#4e6d70" }],
      },
    ],
    retro: [
      { elementType: "geometry", stylers: [{ color: "#ebe3cd" }] },
      { elementType: "labels.text.fill", stylers: [{ color: "#523735" }] },
      { elementType: "labels.text.stroke", stylers: [{ color: "#f5f1e6" }] },
      {
        featureType: "administrative",
        elementType: "geometry.stroke",
        stylers: [{ color: "#c9b2a6" }],
      },
      {
        featureType: "administrative.land_parcel",
        elementType: "geometry.stroke",
        stylers: [{ color: "#dcd2be" }],
      },
      {
        featureType: "administrative.land_parcel",
        elementType: "labels.text.fill",
        stylers: [{ color: "#ae9e90" }],
      },
      {
        featureType: "landscape.natural",
        elementType: "geometry",
        stylers: [{ color: "#dfd2ae" }],
      },
      {
        featureType: "poi",
        elementType: "geometry",
        stylers: [{ color: "#dfd2ae" }],
      },
      {
        featureType: "poi",
        elementType: "labels.text.fill",
        stylers: [{ color: "#93817c" }],
      },
      {
        featureType: "poi.park",
        elementType: "geometry.fill",
        stylers: [{ color: "#a5b076" }],
      },
      {
        featureType: "poi.park",
        elementType: "labels.text.fill",
        stylers: [{ color: "#447530" }],
      },
      {
        featureType: "road",
        elementType: "geometry",
        stylers: [{ color: "#f5f1e6" }],
      },
      {
        featureType: "road.arterial",
        elementType: "geometry",
        stylers: [{ color: "#fdfcf8" }],
      },
      {
        featureType: "road.highway",
        elementType: "geometry",
        stylers: [{ color: "#f8c967" }],
      },
      {
        featureType: "road.highway",
        elementType: "geometry.stroke",
        stylers: [{ color: "#e9bc62" }],
      },
      {
        featureType: "road.highway.controlled_access",
        elementType: "geometry",
        stylers: [{ color: "#e98d58" }],
      },
      {
        featureType: "road.highway.controlled_access",
        elementType: "geometry.stroke",
        stylers: [{ color: "#db8555" }],
      },
      {
        featureType: "road.local",
        elementType: "labels.text.fill",
        stylers: [{ color: "#806b63" }],
      },
      {
        featureType: "transit.line",
        elementType: "geometry",
        stylers: [{ color: "#dfd2ae" }],
      },
      {
        featureType: "transit.line",
        elementType: "labels.text.fill",
        stylers: [{ color: "#8f7d77" }],
      },
      {
        featureType: "transit.line",
        elementType: "labels.text.stroke",
        stylers: [{ color: "#ebe3cd" }],
      },
      {
        featureType: "transit.station",
        elementType: "geometry",
        stylers: [{ color: "#dfd2ae" }],
      },
      {
        featureType: "water",
        elementType: "geometry.fill",
        stylers: [{ color: "#b9d3c2" }],
      },
      {
        featureType: "water",
        elementType: "labels.text.fill",
        stylers: [{ color: "#92998d" }],
      },
    ],
  };

  // Handle style changes internally
  const handleStyleChange = (styleKey) => {
    const newStyle = mapStyles[styleKey] || [];
    setCurrentMapStyle(newStyle);
    if (mapRef.current) {
      mapRef.current.setOptions({ styles: newStyle });
    }
  };

  if (!isLoaded) return <div className="flex items-center justify-center h-64">Loading Map...</div>;

  return (
    <div className={`relative rounded-lg ${className}`} style={{ height }}>
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%', borderRadius: '0.5rem' }}
        center={mapCenter}
        zoom={mapZoom}
        onLoad={(map) => { mapRef.current = map; }}
        options={{ 
          styles: currentMapStyle, 
          fullscreenControl: false,
          streetViewControl: false,
          mapTypeControl: false
        }}
      >
        {/* Geofence polygon - Fixed null check */}
        {geofencePolygon && Array.isArray(geofencePolygon) && geofencePolygon.length > 0 && (
          <PolygonF
            paths={geofencePolygon}
            options={{
              strokeColor: '#FF0000',
              strokeOpacity: 0.8,
              strokeWeight: 2,
              fillColor: '#FF0000',
              fillOpacity: 0.35,
            }}
          />
        )}
      </GoogleMap>

      {/* Map Controls */}
      {showStyleControls && (
        <div className="absolute top-4 right-4 flex flex-col space-y-2">
          <div className="flex space-x-2">
            <button
              onClick={() => handleStyleChange('default')}
              className="w-10 h-10 rounded-full bg-gray-600 text-white flex items-center justify-center hover:bg-gray-700 transition-all border-2 border-gray-300"
              title="Default"
            >
              <SunIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => handleStyleChange('night')}
              className="w-10 h-10 rounded-full bg-gray-600 text-white flex items-center justify-center hover:bg-gray-700 transition-all border-2 border-gray-300"
              title="Night Mode"
            >
              <MoonIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => handleStyleChange('aubergine')}
              className="w-10 h-10 rounded-full bg-gray-600 text-white flex items-center justify-center hover:bg-gray-700 transition-all border-2 border-gray-300"
              title="Aubergine"
            >
              <BoltIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => handleStyleChange('retro')}
              className="w-10 h-10 rounded-full bg-gray-600 text-white flex items-center justify-center hover:bg-gray-700 transition-all border-2 border-gray-300"
              title="Retro"
            >
              <LightBulbIcon className="h-5 w-5" />
            </button>
          </div>
          
          {showMeasurementCount && (
            <div className="bg-white px-3 py-2 rounded-md shadow-md text-sm text-gray-700">
              {measurements.length.toLocaleString()} measurements
              {measurements.length >= MAX_MAP_MARKERS && (
                <div className="text-xs text-orange-600 mt-1">
                  Showing {MAX_MAP_MARKERS.toLocaleString()} max
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MeasurementsMap;